import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, FolderInput, FolderX, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter
} from '@/components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
	addTemplateFolder,
	moveTemplateToFolder
} from '@/actions/templateFolders';

// Moves a template into a folder (null takes it out of any folder).
export const useMoveTemplateToFolder = (isEnterpriseRequested) => {
	const dispatch = useDispatch();

	return React.useCallback(
		async (template, _folder) => {
			const result = await dispatch(
				moveTemplateToFolder({
					_template: template._id,
					_folder,
					isEnterpriseRequested
				})
			);

			if (result?.error) {
				toast.error(result.message || 'Could not move the template');
			}
		},
		[dispatch, isEnterpriseRequested]
	);
};

// "New folder…" from a move menu: creates the folder and moves the template
// into it. Shared by the desktop dropdown and the phone action sheet.
export const CreateFolderAndMoveDialog = ({
	template,
	open,
	onOpenChange,
	isEnterpriseRequested
}) => {
	const dispatch = useDispatch();
	const folders = useSelector((state) => state.templateFolders);
	const move = useMoveTemplateToFolder(isEnterpriseRequested);

	const [newTitle, setNewTitle] = React.useState('');
	const [createError, setCreateError] = React.useState('');

	const closeCreateDialog = () => {
		onOpenChange(false);
		setNewTitle('');
		setCreateError('');
	};

	const createFolderAndMove = async () => {
		const title = newTitle.trim();
		if (!title) {
			setCreateError('Please enter a folder name');
			return;
		}
		if (folders.find((f) => f.title.toLowerCase() === title.toLowerCase())) {
			setCreateError(`A folder named "${title}" already exists`);
			return;
		}

		const result = await dispatch(addTemplateFolder({ title }));
		if (result?.error || !result?.folder?._id) {
			setCreateError(result?.message || 'Could not create the folder');
			return;
		}

		closeCreateDialog();
		move(template, result.folder._id);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(open) => !open && closeCreateDialog()}
		>
			<DialogContent
				mobileVariant="dialog"
				onClick={(e) => e.stopPropagation()}
			>
				<DialogHeader>
					<DialogTitle>Create a folder</DialogTitle>
				</DialogHeader>
				<Input
					placeholder="Folder name"
					value={newTitle}
					maxLength={60}
					autoFocus
					autoComplete="off"
					enterKeyHint="done"
					className="max-md:h-11"
					onChange={(e) => setNewTitle(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							createFolderAndMove();
						}
					}}
				/>
				{createError && (
					<p className="text-sm text-destructive">{createError}</p>
				)}
				<DialogFooter>
					<Button variant="ghost" onClick={closeCreateDialog}>
						Cancel
					</Button>
					<Button onClick={createFolderAndMove}>
						Create & move
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

// Quick "move template to folder" dropdown shown on template cards in
// manager mode. Two clicks per move: open, pick folder.
const MoveToFolderMenu = ({ template, isEnterpriseRequested }) => {
	const folders = useSelector((state) => state.templateFolders);
	const move = useMoveTemplateToFolder(isEnterpriseRequested);

	const [createOpen, setCreateOpen] = React.useState(false);

	const currentFolderId = template._folder ? String(template._folder) : null;

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						onClick={(e) => e.stopPropagation()}
						variant="outline"
						size="sm"
						className="h-8 px-2 gap-1 hover:text-foreground max-md:size-11 max-md:px-0"
						aria-label="Move to folder"
						title="Move to folder"
					>
						<FolderInput className="h-3.5 w-3.5" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="end"
					onClick={(e) => e.stopPropagation()}
				>
					<DropdownMenuLabel>Move to folder</DropdownMenuLabel>
					{folders.map((folder) => (
						<DropdownMenuItem
							key={folder._id}
							onClick={() => move(template, folder._id)}
						>
							<span className="flex-1 truncate pr-2">
								{folder.title}
							</span>
							{currentFolderId === String(folder._id) && (
								<Check className="h-4 w-4 shrink-0" />
							)}
						</DropdownMenuItem>
					))}
					{folders.length > 0 && <DropdownMenuSeparator />}
					{currentFolderId && (
						<DropdownMenuItem onClick={() => move(template, null)}>
							<FolderX className="h-4 w-4 mr-2" />
							Remove from folder
						</DropdownMenuItem>
					)}
					<DropdownMenuItem onClick={() => setCreateOpen(true)}>
						<Plus className="h-4 w-4 mr-2" />
						New folder…
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<CreateFolderAndMoveDialog
				template={template}
				open={createOpen}
				onOpenChange={setCreateOpen}
				isEnterpriseRequested={isEnterpriseRequested}
			/>
		</>
	);
};

export default MoveToFolderMenu;
